/**
 * Grievance terms and the guardian aggression floor (M7 spec §12.3, §8.2; plan S17).
 *
 * - `setGrievance(a, b, floor, untilOnlineDay)`: a **grievance term**. While it is active (`onlineDay < until`) the
 *   ladder's daily update clamps `P(a,b) ≥ floor` after decay (`grievanceFloor`, read by `factions/ladder.ts`); the
 *   relation still follows the ladder (war still needs treasuries and a contested system), so the outcome stays the
 *   sim's. One term per unordered pair: a re-send replaces it (the story bridge re-applies idempotently); a floor ≤ 0
 *   or an end already reached clears it. `'player'` is stored as FACTION_PLAYER and resolved to the corporation's
 *   slot when the ladder reads it (no corporation: the term holds nothing).
 * - `setAggressionFloor('guardian', A, untilOnlineDay)`: stores the cell's floor (S19 applies it in `guardian.ts`).
 * - **Online days only.** Terms and the floor end on their online day (the story slice runs after the relations
 *   slice, so a term holds through the day it ends on). A catch-up passes no online day; a term or floor that would
 *   have ended during the absence is extended until the player has been online `finale.json
 *   floorExtensionOnlineDays` days, with a `floorExtended` event for the away report (the main thread's
 *   `catchUpStory` applies the same rule and re-sends the moved floor).
 *
 * Day work: allocation-free; nothing runs while the store is empty.
 */
import { z } from 'zod'
import { logger } from '../../core/logger'
import finaleJson from '../../data/story/finale.json'
import { emit } from '../events'
import { EV, FACTION, FACTION_IDS, FACTION_NONE, FACTION_PLAYER, SYS_GLOBAL, type CommandResult, type FactionId, type M7Command, type StoryParty, type StrategicState } from '../types'
import { m5FactionName } from '../wire/m5'
import { postStoryWire } from '../wire/wire'

/** The grace the worker shares with the main thread (finale.json, S21 owns the file; only this value is read). */
export const FLOOR_EXTENSION_ONLINE_DAYS: number = z.object({ floorExtensionOnlineDays: z.number().int().min(0) }).parse(finaleJson).floorExtensionOnlineDays

/** The store code of a party: a faction index, or FACTION_PLAYER. */
export function partyCode(party: StoryParty): number {
  return party === 'player' ? FACTION_PLAYER : FACTION[party]
}

/** The faction slot of a party code now: FACTION_PLAYER is the corporation's slot (−1 without one). */
export function partySlot(state: StrategicState, code: number): number {
  return code === FACTION_PLAYER ? state.corp.faction : code
}

/** The faction slot of a party now (−1 = the player without a corporation). */
export function partySlotOf(state: StrategicState, party: StoryParty): number {
  return partySlot(state, partyCode(party))
}

/** A party code's headline name: the corporation's name (or the pilot) for the player's side. */
export function partyName(state: StrategicState, code: number): string {
  if (code === FACTION_PLAYER && state.corp.founded && state.corp.name.length > 0) return state.corp.name
  return m5FactionName(state, code)
}

function isActive(state: StrategicState, until: number): boolean {
  return state.story.onlineDay < until
}

/**
 * The grievance floor on the pair `(a, b)` (faction slots) today: the highest floor of the active terms between
 * them, 0 when none. Allocation-free; returns at once while no term exists (the M4/M5/M6 goldens).
 */
export function grievanceFloor(state: StrategicState, a: number, b: number): number {
  const g = state.story.grievances
  if (g.count === 0) return 0
  let best = 0
  for (let i = 0; i < g.count; i++) {
    if (!isActive(state, g.until[i])) continue
    const x = partySlot(state, g.a[i])
    const y = partySlot(state, g.b[i])
    if (x < 0 || y < 0) continue
    if (((x === a && y === b) || (x === b && y === a)) && g.floor[i] > best) best = g.floor[i]
  }
  return best
}

/** True while the guardian aggression floor holds. */
export function guardianFloorActive(state: StrategicState): boolean {
  const f = state.story.guardianFloor
  return f.value > 0 && isActive(state, f.untilOnlineDay)
}

/** The guardian floor's value while it holds, else 0 (S19 reads it in `guardian.ts`). */
export function guardianFloorValue(state: StrategicState): number {
  return guardianFloorActive(state) ? state.story.guardianFloor.value : 0
}

function findTerm(state: StrategicState, a: number, b: number): number {
  const g = state.story.grievances
  for (let i = 0; i < g.count; i++) {
    if ((g.a[i] === a && g.b[i] === b) || (g.a[i] === b && g.b[i] === a)) return i
  }
  return -1
}

/** Removes term `i` (swaps the last in; the order carries no meaning). */
function removeTerm(state: StrategicState, i: number): void {
  const g = state.story.grievances
  const last = g.count - 1
  g.a[i] = g.a[last]
  g.b[i] = g.b[last]
  g.floor[i] = g.floor[last]
  g.until[i] = g.until[last]
  g.a[last] = 0
  g.b[last] = 0
  g.floor[last] = 0
  g.until[last] = 0
  g.count = last
}

function factionIdOfCode(code: number): FactionId | null {
  return code >= 0 && code < FACTION_IDS.length ? FACTION_IDS[code] : null
}

/** `setGrievance`: sets, replaces or clears the pair's term; a new or raised term runs a Wire item. */
export function applySetGrievance(state: StrategicState, cmd: Extract<M7Command, { type: 'setGrievance' }>): CommandResult {
  const a = partyCode(cmd.a)
  const b = partyCode(cmd.b)
  if (a === b) return { ok: false, error: 'invalid' }
  const g = state.story.grievances
  const i = findTerm(state, a, b)
  if (!(cmd.floor > 0) || cmd.untilOnlineDay <= state.story.onlineDay) {
    if (i >= 0) removeTerm(state, i)
    return { ok: true }
  }
  const max = state.data.charter.ladder.pressure.max
  const floor = cmd.floor > max ? max : cmd.floor
  const raised = i < 0 || floor > g.floor[i]
  let k = i
  if (k < 0) {
    if (g.count >= g.capacity) {
      logger.warn(`story: grievance store full (${g.capacity}); ${cmd.a}/${cmd.b} refused`)
      return { ok: false, error: 'invalid' }
    }
    k = g.count++
  }
  g.a[k] = a
  g.b[k] = b
  g.floor[k] = floor
  g.until[k] = cmd.untilOnlineDay
  emit(state.events, EV.grievanceSet, state.time, SYS_GLOBAL, -1, -1, a, b, -1, 0, floor, 0, cmd.untilOnlineDay)
  if (raised) {
    const factions: FactionId[] = []
    const fa = factionIdOfCode(a)
    const fb = factionIdOfCode(b)
    if (fa !== null) factions.push(fa)
    if (fb !== null) factions.push(fb)
    postStoryWire(state, 'grievanceSet', {
      systemId: state.galaxy.systems[state.world.home].id,
      factions,
      fields: { faction: partyName(state, a), other: partyName(state, b) },
    })
  }
  return { ok: true }
}

/** `setAggressionFloor`: the guardian cell's floor (the only faction with one); a value ≤ 0 or a past end clears it. */
export function applySetAggressionFloor(state: StrategicState, cmd: Extract<M7Command, { type: 'setAggressionFloor' }>): CommandResult {
  if (cmd.faction !== 'guardian') return { ok: false, error: 'invalid' }
  const f = state.story.guardianFloor
  if (!(cmd.value > 0) || cmd.untilOnlineDay <= state.story.onlineDay) {
    f.value = 0
    f.untilOnlineDay = -1
    return { ok: true }
  }
  f.value = cmd.value
  f.untilOnlineDay = cmd.untilOnlineDay
  return { ok: true }
}

/** Catch-up days run so far in the current offline window (runtime only; the first online day resets it). */
let offlineRun = 0
let offlineOf: StrategicState | null = null

/** The end of a term after `offlineRun` catch-up days: the grace end when it would have run out by now. */
function extended(state: StrategicState, until: number): number {
  const online = state.story.onlineDay
  const hold = online + FLOOR_EXTENSION_ONLINE_DAYS
  if (until <= online || until >= hold || until - online > offlineRun) return until
  return hold
}

/**
 * The story day work: expiry on online days; on catch-up days, the offline extension (once per term, with a
 * `floorExtended` event: faction = a, other = b, value = the floor, aux = the new end). Allocation-free.
 */
export function stepGrievanceDay(state: StrategicState): void {
  const st = state.story
  const g = st.grievances
  const floor = st.guardianFloor
  if (st.offline) {
    if (offlineOf !== state) {
      offlineOf = state
      offlineRun = 0
    }
    offlineRun++
    for (let i = 0; i < g.count; i++) {
      const next = extended(state, g.until[i])
      if (next === g.until[i]) continue
      g.until[i] = next
      emit(state.events, EV.floorExtended, state.time, SYS_GLOBAL, -1, -1, g.a[i], g.b[i], -1, 0, g.floor[i], 0, next)
    }
    if (floor.value > 0) {
      const next = extended(state, floor.untilOnlineDay)
      if (next !== floor.untilOnlineDay) {
        floor.untilOnlineDay = next
        emit(state.events, EV.floorExtended, state.time, SYS_GLOBAL, -1, -1, FACTION.guardian, FACTION_NONE, -1, 0, floor.value, 0, next)
      }
    }
    return
  }
  offlineRun = 0
  offlineOf = null
  for (let i = g.count - 1; i >= 0; i--) if (!isActive(state, g.until[i])) removeTerm(state, i)
  if (floor.value > 0 && !isActive(state, floor.untilOnlineDay)) {
    floor.value = 0
    floor.untilOnlineDay = -1
  }
}
